import { Panel } from "primereact/panel"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { useMemo, useState } from "react"
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"
import { formatFloat, formatNumber } from "@/utils/format"
import { Product_Output, ProductionTask_Input } from "@/api"
import { Dialog } from "primereact/dialog"
import { getChartColors } from "./charts/commonChart"
import SeparablePanel from "@/components/SeparablePanel"

interface DateSummaryProps {
  selectedDate: string
  productsById: Record<string, Product_Output>
  productionTasks: ProductionTask_Input[]
  holidayDates?: string[]
}

interface ProductionSummaryItem {
  sku_id: string
  name: string
  category?: string
  production_qty: number
  total_duration: number
  machine_sheet?: string | null
}

interface ManufacturingSummaryItem {
  category: string
  total_duration: number
}

interface MachineUsageRow {
  machine_id: string
  duration: number
  utilization: number
  task_count: number
  total_qty: number
}

const DateSummary = ({
  selectedDate,
  productsById,
  productionTasks,
  holidayDates = []
}: DateSummaryProps) => {
  const [taskModal, setTaskModal] = useState<{ machine: string; tasks: ProductionTask_Input[] }>({ machine: "", tasks: [] })

  const holidaySet = useMemo(() => new Set(holidayDates), [holidayDates])
  const dayBaseSeconds = 24 * 60 * 60

  const tasksForDay = useMemo(
    () => productionTasks.filter((t) => t.date === selectedDate),
    [productionTasks, selectedDate]
  )

  const previousProductionInfo = useMemo(() => {
    const datesWithTasks = productionTasks
      .map((t) => t.date)
      .filter((d) => d !== selectedDate && !holidaySet.has(d))
    if (!datesWithTasks.length) {
      return { date: null as string | null, items: new Set<string>() }
    }
    const prevDate = datesWithTasks.sort().reverse().find((d) => d < selectedDate) ?? null
    if (!prevDate) return { date: null, items: new Set<string>() }

    const prevItems = new Set(
      productionTasks.filter((t) => t.date === prevDate && (t.quantity ?? 0) > 0).map((t) => t.sku_id)
    )
    return { date: prevDate, items: prevItems }
  }, [holidaySet, productionTasks, selectedDate])

  const producedItems = useMemo<ProductionSummaryItem[]>(() => {
    const acc = new Map<string, ProductionSummaryItem>()
    const getQuantityPerCycle = (task: ProductionTask_Input) => {
      const variant = productsById[task.sku_id]?.machine_variants?.[task.machine_id]
      const fallback = productsById[task.sku_id]?.quantity_per_cycle
      const qpc = (variant as any)?.quantity_per_cycle ?? fallback ?? 1
      return qpc || 1
    }
    tasksForDay.forEach((task) => {
      const product = productsById[task.sku_id]
      const existing = acc.get(task.sku_id)
      const qty = task.quantity ?? 0
      const duration = task.duration ?? 0
      if (existing) {
        existing.production_qty += qty
        existing.total_duration += duration
      } else {
        acc.set(task.sku_id, {
          sku_id: task.sku_id,
          name: product?.name ?? task.sku_id,
          category: product?.category,
          production_qty: qty,
          total_duration: duration,
          machine_sheet: product?.machine_sheet,
        })
      }
    })
    return Array.from(acc.values()).sort((a, b) => b.production_qty - a.production_qty)
  }, [productsById, tasksForDay])

  const highlightedItemIds = useMemo(() => {
    return producedItems
      .filter(item => previousProductionInfo.items.has(item.sku_id))
      .map(item => item.sku_id)
  }, [producedItems, previousProductionInfo.items])

  const highlightActive = highlightedItemIds.length > 0

  const totalProduction = useMemo(() => producedItems.reduce((sum, item) => sum + item.production_qty, 0), [producedItems])

  const categoryCount = useMemo(() => {
    const categories = new Set(producedItems.map(item => item.category ?? "未分類"))
    return categories.size
  }, [producedItems])

  const totalProcessTime = useMemo(() => {
    return producedItems.reduce((sum, item) => sum + item.total_duration , 0)
  }, [producedItems])

  const totalBoxCount = useMemo(() => {
    return tasksForDay.reduce((sum, task) => sum + (task.box_count ?? 0), 0)
  }, [tasksForDay])

  const totalPlCount = useMemo(() => {
    return tasksForDay.reduce((sum, task) => sum + (task.pl_count ?? 0), 0)
  }, [tasksForDay])

  const materialUsage = useMemo(() => {
    const usage = new Map<string, number>()
    tasksForDay.forEach((task) => {
      const product = productsById[task.sku_id]
      const materialName = product?.material1 ?? product?.material2 ?? "未設定"
      const kg = task.material_kg ?? 0
      if (kg <= 0) return
      usage.set(materialName, (usage.get(materialName) ?? 0) + kg)
    })
    return Array.from(usage.entries())
      .map(([name, kg]) => ({ name, kg }))
      .sort((a, b) => b.kg - a.kg)
  }, [productsById, tasksForDay])

  const manufacturingSummary = useMemo<ManufacturingSummaryItem[]>(() => {
    const summaryMap = new Map<string, number>()
    producedItems.forEach(item => {
      const key = item.category ?? '未分類'
      summaryMap.set(key, (summaryMap.get(key) ?? 0) + item.total_duration)
    })

    return Array.from(summaryMap.entries())
      .map(([category, time]) => ({ category, total_duration: time }))
      .sort((a, b) => b.total_duration - a.total_duration)
  }, [producedItems])

  const manufacturingChartData = useMemo(() => {
    return manufacturingSummary.map((item, index) => ({
      name: item.category,
      value: item.total_duration / 3600,
      color: getChartColors(index),
    }))
  }, [manufacturingSummary])

  const machineIds = useMemo(() => {
    const ids = new Set<string>()
    Object.values(productsById).forEach((product) => {
      Object.keys(product.machine_variants ?? {}).forEach((machineId) => ids.add(machineId))
    })
    tasksForDay.forEach((task) => ids.add(task.machine_id))
    return Array.from(ids).sort((a, b) => a.localeCompare(b))
  }, [productsById, tasksForDay])

  const machineUsage = useMemo<MachineUsageRow[]>(() => {
    const usage = new Map<string, MachineUsageRow>()
    machineIds.forEach((machineId) => {
      usage.set(machineId, {
        machine_id: machineId,
        duration: 0,
        utilization: 0,
        task_count: 0,
        total_qty: 0,
      })
    })
    const getQuantityPerCycle = (task: ProductionTask_Input) => {
      const qpc = productsById[task.sku_id]?.quantity_per_cycle
      return qpc || 1
    }
    tasksForDay.forEach((task) => {
      const key = task.machine_id
      const qty = task.quantity ?? 0
      const duration = task.duration
      const prev = usage.get(key)
      if (!prev) {
        usage.set(key, {
          machine_id: key,
          duration,
          utilization: 0,
          task_count: 1,
          total_qty: qty,
        })
        return
      }
      prev.duration += duration
      prev.total_qty += qty
      prev.task_count += 1
    })
    return machineIds.map((machineId) => {
      const usageRow = usage.get(machineId)
      if (!usageRow) {
        return {
          machine_id: machineId,
          duration: 0,
          utilization: 0,
          task_count: 0,
          total_qty: 0,
        }
      }
      return {
        ...usageRow,
        utilization: dayBaseSeconds > 0 ? usageRow.duration / dayBaseSeconds : 0,
      }
    })
  }, [dayBaseSeconds, machineIds, productsById, tasksForDay])

  const heatColor = (utilization: number) => {
    const u = Math.max(0, Math.min(1, utilization))
    const hue = 120 - u * 120 // 120 (green) to 0 (red)
    return `hsl(${hue}, 80%, 75%)`
  }

  const openTaskModal = (machineId: string) => {
    const tasks = tasksForDay.filter((t) => t.machine_id === machineId)
    setTaskModal({ machine: machineId, tasks })
  }

  const closeTaskModal = () => setTaskModal({ machine: "", tasks: [] })

  return (
    <div className="flex flex-column gap-2">
      <h3>{selectedDate}のサマリ</h3>
      <Panel
        header={`この日に生産された品目 (合計: ${totalProduction}個, 分類数: ${categoryCount}, 総成形時間: ${formatFloat(totalProcessTime / 3600, 2)}h)`}
        className={`w-full ${highlightActive ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}
        toggleable
      >
        {highlightActive && (
          <div className="text-orange-600 text-sm font-semibold mb-3 flex gap-2 flex-wrap">
            <span>前回稼働日({previousProductionInfo.date ?? '不明'})に生産された品目:</span>
            <span className="font-mono text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
              {highlightedItemIds.join(', ')}
            </span>
          </div>
        )}

        {producedItems.length > 0 ? (
          <>
            <div className="text-sm text-gray-700 mb-3">
              合計生産数: {totalProduction}個 / 分類数: {categoryCount} / 総成形時間: {formatFloat(totalProcessTime / 3600, 2)}h / 総箱数: {formatNumber(totalBoxCount)}箱 / 総PL数: {formatNumber(totalPlCount)}PL
            </div>
            <DataTable
              value={producedItems}
              size="small"
              stripedRows
              scrollable
              scrollHeight="360px"
              rowClassName={(rowData: ProductionSummaryItem) => ({
                'border-l-4 border-orange-400 bg-orange-50/70': previousProductionInfo.items.has(rowData.sku_id)
              })}
            >
              <Column field="sku_id" header="品目ID" sortable />
              <Column field="name" header="品目名" sortable />
              <Column field="category" header="分類" sortable />
              <Column field="machine_sheet" header="優先設備" sortable />
              <Column
                field="production_qty"
                header="生産数"
                body={(rowData: ProductionSummaryItem) => formatNumber(rowData.production_qty)}
                sortable
              />
              <Column
                field="total_duration"
                header="成形時間(h)"
                body={(rowData: ProductionSummaryItem) => formatFloat(rowData.total_duration / 3600, 2)}
                sortable
              />
            </DataTable>
            {materialUsage.length > 0 && (
              <div className="mt-4">
                <h4>材料使用量</h4>
                <DataTable value={materialUsage} size="small" stripedRows>
                  <Column field="name" header="材料" sortable />
                  <Column
                    field="kg"
                    header="使用量(kg)"
                    body={(rowData: { name: string; kg: number }) => formatFloat(rowData.kg, 2)}
                    sortable
                  />
                </DataTable>
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-4 text-600">
            この日は生産された品目がありません
          </div>
        )}
      </Panel>

      <SeparablePanel header="分類別 成形時間分布" toggleable>
            {manufacturingSummary.length > 0 && (
              <div className="mt-0">
                <div className="w-full" style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={manufacturingChartData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={90}
                        labelLine={false}
                        label={(entry) => `${entry.name} ${formatFloat(entry.value, 1)}h`}
                      >
                        {manufacturingChartData.map((entry) => (
                          <Cell key={`cell-${entry.name}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${formatFloat(Number(value), 2)}h`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )
          }
      </SeparablePanel>

      <Panel header="成形機 使用率" toggleable>
        {machineUsage.length === 0 ? (
          <div className="text-center p-4 text-600">この日の成形タスクはありません</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm table-auto border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">成形機</th>
                  <th className="p-2 text-right">使用時間(h)</th>
                  <th className="p-2 text-right">使用率</th>
                  <th className="p-2 text-right">タスク数</th>
                  <th className="p-2 text-right">数量</th>
                </tr>
              </thead>
              <tbody>
                {machineUsage.map((row) => (
                  <tr key={row.machine_id} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2 whitespace-nowrap">{row.machine_id}</td>
                    <td className="p-2 text-right">{formatFloat(row.duration / 3600, 2)}</td>
                    <td
                      className="p-2 text-right"
                      style={{ backgroundColor: heatColor(row.utilization) }}
                    >
                      {formatFloat(row.utilization * 100, 1)}%
                    </td>
                    <td className="p-2 text-right">
                      <span
                        className="underline text-blue-700 cursor-pointer"
                        onClick={() => openTaskModal(row.machine_id)}
                      >
                        {row.task_count}
                      </span>
                    </td>
                    <td className="p-2 text-right">{formatNumber(row.total_qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-gray-500 mt-2">* 24時間を基準に算出。ヒートマップは使用率を色で表現</div>
          </div>
        )}
      </Panel>

      <Dialog
        header={`成形タスク (${taskModal.machine || ""})`}
        visible={!!taskModal.machine}
        style={{ width: "40rem" }}
        onHide={closeTaskModal}
      >
        {taskModal.tasks.length === 0 ? (
          <div className="text-sm text-gray-600">この成形機のタスクはありません。</div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm table-auto border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">品目ID</th>
                  <th className="p-2 text-right">数量</th>
                  <th className="p-2 text-right">成形時間(h)</th>
                  <th className="p-2 text-left">固定</th>
                </tr>
              </thead>
              <tbody>
                {taskModal.tasks
                  .slice()
                  .sort((a, b) => a.sku_id.localeCompare(b.sku_id))
                  .map((task) => {
                    const product = productsById[task.sku_id]
                    const qpc = (productsById[task.sku_id]?.machine_variants?.[task.machine_id] as any)?.quantity_per_cycle
                      ?? productsById[task.sku_id]?.quantity_per_cycle
                      ?? 1
                    const duration = (task.duration ?? 0)
                    return (
                      <tr key={task.task_id} className="odd:bg-white even:bg-gray-50">
                        <td className="p-2 whitespace-nowrap">{task.sku_id}</td>
                        <td className="p-2 text-right">{formatNumber(task.quantity ?? 0)}</td>
                        <td className="p-2 text-right">{formatFloat(duration / 3600, 2)}</td>
                        <td className="p-2 whitespace-nowrap">{task.is_fixed ? "固定" : ""}</td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        )}
      </Dialog>
    </div>
  )
}

export default DateSummary
